/**
 * Canonical site URL for SEO (sitemap, robots, JSON-LD, Open Graph).
 * Update if production domain changes.
 */
export const SITE_ORIGIN = 'https://reffconnect.com';

export const SITE_NAME = 'ReffConnect';

export const DEFAULT_DESCRIPTION =
  'ReffConnect helps job seekers in India with career guidance, resume reviews, and verified job referrals from top companies.';

export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/assets/Images/ogreff.png`;

export type RouteSeo = {
  title: string;
  description: string;
  /** Optional robots directive (e.g. auth flows). */
  robots?: string;
};

const ROUTE_SEO: Record<string, RouteSeo> = {
  '/': {
    title: `${SITE_NAME} — Trusted Career Guidance & Job Referrals in India`,
    description: DEFAULT_DESCRIPTION,
  },
  '/landing': {
    title: `${SITE_NAME} — Trusted Career Guidance & Job Referrals in India`,
    description: DEFAULT_DESCRIPTION,
    robots: 'noindex, follow',
  },
  '/our-mission': {
    title: `Our Mission | ${SITE_NAME}`,
    description:
      'How ReffConnect builds faster, fairer hiring through trusted referrals and verified networks for candidates and employers.',
  },
  '/services': {
    title: `Job Referral Services in India | ${SITE_NAME}`,
    description:
      'Explore referral-first services: verified employee referrals, mentor sessions, and resume reviews for job seekers.',
  },
  '/about': {
    title: `About Us | ${SITE_NAME}`,
    description:
      'Meet the ReffConnect team and learn how we connect talent with opportunity through referrals and product-led hiring.',
  },
  '/career': {
    title: `Careers | ${SITE_NAME}`,
    description:
      'Explore career opportunities at ReffConnect and help build India\'s trusted referral-first hiring platform.',
  },
  '/for-job-seekers': {
    title: `For Job Seekers — Get Referred at Top Companies in India | ${SITE_NAME}`,
    description:
      'Job seekers: get verified referrals, mock interviews, and resume reviews from insiders at Google, Amazon, Flipkart, and 200+ companies in India.',
  },
  '/for-professionals': {
    title: `For Professionals — Earn by Referring & Mentoring | ${SITE_NAME}`,
    description:
      'Professionals: monetize your network by providing referrals, mock interviews, and career guidance to job seekers targeting your company.',
  },
  '/resume-review': {
    title: `Resume Review by Hiring Insiders in India | ${SITE_NAME}`,
    description:
      'Get line-by-line resume feedback from verified hiring managers and recruiters at top Indian tech companies. Fix gaps before you apply.',
  },
  '/mock-interview': {
    title: `Mock Interviews with Real Company Insiders | ${SITE_NAME}`,
    description:
      'Practice interviews with verified employees from your target company. AI-matched by role, skill level, and hiring bar. Score every response.',
  },
  '/refer-session': {
    title: `1-on-1 Career Guidance Calls with Industry Insiders | ${SITE_NAME}`,
    description:
      'Book 1-on-1 career guidance sessions with professionals at top companies. Get personalized advice on roles, teams, and interview prep.',
  },
  '/week-refer-drop': {
    title: `Weekly Refer Drop — Curated Referral Opportunities | ${SITE_NAME}`,
    description:
      'Get weekly curated batches of referral opportunities across Google, Amazon, Microsoft, and top startups in India. First come, first served.',
  },
  '/contact-us': {
    title: `Contact Us | ${SITE_NAME}`,
    description: 'Contact ReffConnect for product questions, partnerships, press, or support. We respond quickly.',
  },
  '/legal': {
    title: `Legal Center | ${SITE_NAME}`,
    description:
      'India-focused legal center: compliance notices, grievance process, and links to privacy, terms, and cookie policies.',
  },
  '/faq': {
    title: `FAQ | ${SITE_NAME}`,
    description: 'Answers to common questions about ReffConnect referrals, accounts, credits, privacy, and using the platform safely.',
  },
  '/terms': {
    title: `Terms and Conditions | ${SITE_NAME}`,
    description: 'Terms governing use of the ReffConnect platform, credits, referrals, accounts, and acceptable use.',
  },
  '/cookie-policy': {
    title: `Cookie Policy | ${SITE_NAME}`,
    description: 'How ReffConnect uses cookies and similar technologies, categories of cookies, and your choices.',
  },
  '/login': {
    title: `Sign In | ${SITE_NAME}`,
    description: 'Sign in to your ReffConnect account to manage referrals, credits, and profile.',
    robots: 'noindex, follow',
  },
  '/signup': {
    title: `Create Account | ${SITE_NAME}`,
    description: 'Create a ReffConnect account to request referrals and join the verified referral marketplace.',
    robots: 'noindex, follow',
  },
  '/forgot-password': {
    title: `Forgot Password | ${SITE_NAME}`,
    description: 'Reset your ReffConnect account password securely.',
    robots: 'noindex, follow',
  },
  '/verify-email': {
    title: `Verify Email | ${SITE_NAME}`,
    description: 'Confirm your email address to activate your ReffConnect account.',
    robots: 'noindex, follow',
  },
  '/onboarding': {
    title: `Onboarding | ${SITE_NAME}`,
    description: 'Complete your ReffConnect profile to get better referral matches.',
    robots: 'noindex, follow',
  },
  '/oauth/google/success': {
    title: `Signing you in… | ${SITE_NAME}`,
    description: 'Completing Google sign-in for ReffConnect.',
    robots: 'noindex, follow',
  },
};

/** These routes define their own `<Helmet>` (title, description, etc.). Global SEO only adds canonical / graph. */
export function pathHasDedicatedHelmet(pathname: string): boolean {
  if (
    pathname === '/privacy' ||
    pathname === '/terms' ||
    pathname === '/legal' ||
    pathname === '/guides' ||
    pathname === '/case-study' ||
    pathname === '/blog'
  ) {
    return true;
  }
  if (pathname.startsWith('/blog/')) return true;
  return false;
}

export function absoluteUrl(pathname: string): string {
  const path = pathname && pathname !== '' ? pathname : '/';
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return new URL(normalized, SITE_ORIGIN).href;
}

/** Returns `null` when the page component owns meta tags via Helmet. */
export function getSeoForPath(pathname: string): RouteSeo | null {
  if (pathHasDedicatedHelmet(pathname)) return null;
  const exact = ROUTE_SEO[pathname];
  if (exact) return exact;
  return ROUTE_SEO['/']!;
}

export function getJsonLdOrganizationAndWebsite(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_ORIGIN}/#organization`,
        name: SITE_NAME,
        url: SITE_ORIGIN,
        logo: {
          '@type': 'ImageObject',
          url: DEFAULT_OG_IMAGE,
          width: 1200,
          height: 630,
        },
        description: DEFAULT_DESCRIPTION,
        foundingDate: '2024',
        areaServed: {
          '@type': 'Country',
          name: 'India',
        },
        sameAs: [
          'https://www.linkedin.com/company/reffconnect',
          'https://twitter.com/Reffconnect',
          'https://www.instagram.com/reffconnect',
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          url: `${SITE_ORIGIN}/contact-us`,
          contactType: 'customer support',
          availableLanguage: ['English', 'Hindi'],
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_ORIGIN}/#website`,
        name: SITE_NAME,
        url: SITE_ORIGIN,
        publisher: { '@id': `${SITE_ORIGIN}/#organization` },
        inLanguage: 'en-IN',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${SITE_ORIGIN}/blog?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Service',
        '@id': `${SITE_ORIGIN}/#referral-service`,
        name: 'Verified Job Referrals',
        description: 'Get internal referrals from verified employees at Google, Amazon, Flipkart, Microsoft, and 200+ top companies in India.',
        provider: { '@id': `${SITE_ORIGIN}/#organization` },
        serviceType: 'Job Referral',
        areaServed: { '@type': 'Country', name: 'India' },
        url: `${SITE_ORIGIN}/for-job-seekers`,
      },
      {
        '@type': 'Service',
        '@id': `${SITE_ORIGIN}/#mock-interview`,
        name: 'Mock Interviews with Company Insiders',
        description: 'Practice interviews with verified employees from your target company. AI-matched by role, skill level, and hiring bar.',
        provider: { '@id': `${SITE_ORIGIN}/#organization` },
        serviceType: 'Mock Interview',
        areaServed: { '@type': 'Country', name: 'India' },
        url: `${SITE_ORIGIN}/mock-interview`,
      },
      {
        '@type': 'Service',
        '@id': `${SITE_ORIGIN}/#resume-review`,
        name: 'Resume Review by Hiring Insiders',
        description: 'Line-by-line resume feedback from hiring managers and recruiters who review resumes daily at top Indian tech companies.',
        provider: { '@id': `${SITE_ORIGIN}/#organization` },
        serviceType: 'Resume Review',
        areaServed: { '@type': 'Country', name: 'India' },
        url: `${SITE_ORIGIN}/resume-review`,
      },
      {
        '@type': 'Service',
        '@id': `${SITE_ORIGIN}/#refer-session`,
        name: '1-on-1 Career Guidance Calls',
        description: 'Book personalized career guidance sessions with professionals at top companies for advice on roles, teams, and interview prep.',
        provider: { '@id': `${SITE_ORIGIN}/#organization` },
        serviceType: 'Career Counseling',
        areaServed: { '@type': 'Country', name: 'India' },
        url: `${SITE_ORIGIN}/refer-session`,
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE_ORIGIN}/#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
          { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_ORIGIN}/services` },
          { '@type': 'ListItem', position: 3, name: 'For Job Seekers', item: `${SITE_ORIGIN}/for-job-seekers` },
          { '@type': 'ListItem', position: 4, name: 'For Professionals', item: `${SITE_ORIGIN}/for-professionals` },
          { '@type': 'ListItem', position: 5, name: 'Blog', item: `${SITE_ORIGIN}/blog` },
          { '@type': 'ListItem', position: 6, name: 'About', item: `${SITE_ORIGIN}/about` },
          { '@type': 'ListItem', position: 7, name: 'Contact', item: `${SITE_ORIGIN}/contact-us` },
        ],
      },
      // Speakable — tells Google which content is suitable for voice/AI reading
      {
        '@type': 'WebPage',
        '@id': `${SITE_ORIGIN}/#homepage`,
        url: SITE_ORIGIN,
        name: `${SITE_NAME} — Verified Job Referrals & Hiring Marketplace in India`,
        isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
        about: [
          { '@type': 'Thing', name: 'Job Referrals', sameAs: 'https://en.wikipedia.org/wiki/Referral' },
          { '@type': 'Thing', name: 'Recruitment', sameAs: 'https://en.wikipedia.org/wiki/Recruitment' },
          { '@type': 'Thing', name: 'Mock Interview', sameAs: 'https://en.wikipedia.org/wiki/Mock_interview' },
          { '@type': 'Thing', name: 'Career Counseling', sameAs: 'https://en.wikipedia.org/wiki/Career_counseling' },
          { '@type': 'Thing', name: 'Resume', sameAs: 'https://en.wikipedia.org/wiki/R%C3%A9sum%C3%A9' },
        ],
        mentions: [
          { '@type': 'Organization', name: 'Google', sameAs: 'https://en.wikipedia.org/wiki/Google' },
          { '@type': 'Organization', name: 'Amazon', sameAs: 'https://en.wikipedia.org/wiki/Amazon_(company)' },
          { '@type': 'Organization', name: 'Microsoft', sameAs: 'https://en.wikipedia.org/wiki/Microsoft' },
          { '@type': 'Organization', name: 'Flipkart', sameAs: 'https://en.wikipedia.org/wiki/Flipkart' },
          { '@type': 'Organization', name: 'Meta Platforms', sameAs: 'https://en.wikipedia.org/wiki/Meta_Platforms' },
        ],
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['.hero-title', '.hero-subtitle', '.sh-title', '.sh-description'],
        },
      },
    ],
  };
}
